/*
 * ET - PDP Testimonials (sections/et-pdp-testimonials.liquid)
 * <et-pdp-testimonials> wraps .swiper + the progress pill. Swiper comes from window.etLoadSwiper() (et-base-head).
 * Card widths live in CSS (3 per row from 990px, 308px on tablet, 280px on mobile), so Swiper runs with
 * slidesPerView 'auto' at every width and follows the stylesheet on resize. watchOverflow locks the slider and hides
 * the progress pill while every card fits (the static 3-card desktop row); extra cards and smaller screens swipe.
 * The element creates one Swiper when connected and destroys it when disconnected, so a theme editor re-render
 * (shopify:section:load / unload) never leaves a duplicate instance. Selected blocks slide into view.
 * Optional, from the section settings: data-autoplay + data-autoplay-delay (ms; paused on hover, stopped while a block
 * is selected in the editor), data-rewind (back to the first card after the last) and prev/next arrows
 * ([data-et-pt-prev] / [data-et-pt-next]). The element gets et-pdp-testimonials__slider--locked while every card fits.
 */
(function () {
  'use strict';

  if (!window.customElements || window.customElements.get('et-pdp-testimonials')) return;

  const SLIDE_SELECTOR = '[data-et-pt-slide]';

  /*
   * Progress thumb length = the part of the slide pitch (card + gap) that is in view, rounded to whole pixels.
   * Swiper's 'auto' size (viewport / slides without the last gap) gives 37.65px for 3 mobile cards; the Figma thumb is
   * 39px (96 x 358 / 888 = 38.7). Still proportional, so more or fewer cards lengthen or shorten the thumb.
   * Swiper's own scrollbar handlers run first on each event; this one then resizes the thumb and puts it back in place.
   */
  const fitScrollbar = (swiper) => {
    const scrollbar = swiper.scrollbar;
    if (!scrollbar || !scrollbar.el || !scrollbar.dragEl || typeof scrollbar.updateSize !== 'function') return;

    const trackSize = scrollbar.el.offsetWidth;
    const spacing = parseFloat(swiper.params.spaceBetween) || 0;
    const sizes = swiper.slidesSizesGrid || [];
    const total = sizes.reduce((sum, size) => sum + size + spacing, 0);
    if (!trackSize || !total || !swiper.size) return;

    const dragSize = Math.min(trackSize, Math.max(1, Math.round((trackSize * (swiper.size + spacing)) / total)));
    if (swiper.params.scrollbar.dragSize === dragSize) return;

    swiper.params.scrollbar.dragSize = dragSize;
    scrollbar.updateSize();
    scrollbar.setTranslate();
  };

  const LOCKED_CLASS = 'et-pdp-testimonials__slider--locked';

  class EtPdpTestimonials extends HTMLElement {
    connectedCallback() {
      if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', () => this.init(), { once: true });
      } else {
        this.init();
      }
    }

    disconnectedCallback() {
      this.destroy();
    }

    get options() {
      const scrollbarEl = this.querySelector('[data-et-pt-scrollbar]');
      const prevEl = this.querySelector('[data-et-pt-prev]');
      const nextEl = this.querySelector('[data-et-pt-next]');
      const syncLock = (swiper) => this.classList.toggle(LOCKED_CLASS, Boolean(swiper.isLocked));
      const onLockChange = (swiper) => {
        fitScrollbar(swiper);
        syncLock(swiper);
      };

      const options = {
        slidesPerView: 'auto',
        spaceBetween: 16,
        speed: 400,
        watchOverflow: true,
        grabCursor: true,
        scrollbar: scrollbarEl ? { el: scrollbarEl, draggable: true, snapOnRelease: true } : false,
        a11y: { enabled: true },
        on: {
          init: onLockChange,
          update: fitScrollbar,
          resize: fitScrollbar,
          observerUpdate: fitScrollbar,
          lock: onLockChange,
          unlock: onLockChange
        }
      };

      if (prevEl && nextEl) options.navigation = { prevEl, nextEl };
      if (this.dataset.rewind === 'true') options.rewind = true;

      const autoplay = this.autoplayOptions();
      if (autoplay) options.autoplay = autoplay;

      return options;
    }

    autoplayOptions() {
      if (this.dataset.autoplay !== 'true') return false;
      const reducedMotion =
        typeof window.matchMedia === 'function' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      if (reducedMotion) return false;
      const delay = parseInt(this.dataset.autoplayDelay, 10) || 5000;
      return { delay, disableOnInteraction: false, pauseOnMouseEnter: true };
    }

    init() {
      const swiperEl = this.querySelector('[data-et-pt-swiper]');
      if (!swiperEl || this.swiper || this.loading || typeof window.etLoadSwiper !== 'function') return;

      this.loading = true;
      window.etLoadSwiper().then((Swiper) => {
        this.loading = false;
        if (!Swiper || !this.isConnected || this.swiper) return;
        this.swiper = new Swiper(swiperEl, this.options);
        if (this.pendingIndex !== undefined) {
          this.stopAutoplay();
          this.swiper.slideTo(this.pendingIndex, 0);
          this.pendingIndex = undefined;
        }
      });
    }

    destroy() {
      if (!this.swiper) return;
      this.swiper.destroy(true, true);
      this.swiper = null;
      this.classList.remove(LOCKED_CLASS);
    }

    stopAutoplay() {
      if (this.swiper && this.swiper.autoplay && this.swiper.autoplay.running) this.swiper.autoplay.stop();
    }

    resumeAutoplay() {
      if (this.swiper && this.swiper.params.autoplay && this.swiper.params.autoplay.enabled && this.swiper.autoplay) {
        this.swiper.autoplay.start();
      }
    }

    selectSlide(slide) {
      const index = parseInt(slide.dataset.etPtIndex, 10);
      if (Number.isNaN(index)) return;
      if (!this.swiper) {
        this.pendingIndex = index;
        return;
      }
      this.stopAutoplay();
      this.swiper.slideTo(index, 0);
    }
  }

  window.customElements.define('et-pdp-testimonials', EtPdpTestimonials);

  const slidersIn = (scope) =>
    scope instanceof Element || scope === document ? Array.from(scope.querySelectorAll('et-pdp-testimonials')) : [];

  document.addEventListener('shopify:section:load', (event) => slidersIn(event.target).forEach((el) => el.init()));
  document.addEventListener('shopify:section:unload', (event) => slidersIn(event.target).forEach((el) => el.destroy()));

  document.addEventListener('shopify:block:select', (event) => {
    const slide = event.target instanceof Element ? event.target.closest(SLIDE_SELECTOR) : null;
    const slider = slide && slide.closest('et-pdp-testimonials');
    if (slider) slider.selectSlide(slide);
  });

  document.addEventListener('shopify:block:deselect', (event) => {
    const slider = event.target instanceof Element ? event.target.closest('et-pdp-testimonials') : null;
    if (slider) slider.resumeAutoplay();
  });
})();
